/**
 * MonthlyGeneratorDialog — Generează automat foi de parcurs pe o lună întreagă.
 *
 * Flux:
 *  1. Admin alege șofer, mașină, lună/an, km start/end, șantiere.
 *  2. Apel POST /api/trip-sheets/generate-monthly/preview → returnează propuneri.
 *  3. Admin verifică/editează tabelul, apoi POST /commit pentru a salva tot.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Trash2, Plus, Loader2, AlertCircle, CheckCircle2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RO_MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

export default function MonthlyGeneratorDialog({ open, onClose, companyId, workers, vehicles, onSaved }) {
  const now = new Date();
  const [workerId, setWorkerId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [startKm, setStartKm] = useState("");
  const [endKm, setEndKm] = useState("");
  const [siteAddresses, setSiteAddresses] = useState([{ name: "", address: "" }]);
  const [useAi, setUseAi] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPreview(null);
    }
  }, [open]);

  // Load active sites from this company to suggest addresses
  useEffect(() => {
    if (!open || !companyId) return;
    axios
      .get(`${API}/sites?company_id=${companyId}`)
      .then((r) => setAvailableSites(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [open, companyId]);

  const workerObj = useMemo(() => workers.find((w) => w.id === workerId), [workers, workerId]);
  const vehicleObj = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);

  // Auto-prefill KM Start cu km curent al vehiculului (la momentul emiterii foilor).
  // KM End rămâne opțional — admin îl completează ulterior pe fiecare foaie individual.
  const [autoPrefilledKm, setAutoPrefilledKm] = useState(null);
  useEffect(() => {
    if (!vehicleId) return;
    let alive = true;
    axios
      .get(`${API}/vehicles/${vehicleId}/last-km`)
      .then((r) => {
        if (!alive) return;
        const km = Number(r.data?.last_km || 0);
        if (km > 0) {
          setStartKm(String(km));
          setAutoPrefilledKm({ value: km, source: r.data?.source || "vehicle" });
        }
      })
      .catch(() => { /* fallback la valoarea introdusă manual */ });
    return () => { alive = false; };
  }, [vehicleId]);

  const handleAddSite = () => setSiteAddresses((s) => [...s, { name: "", address: "" }]);
  const handleRemoveSite = (idx) => setSiteAddresses((s) => s.filter((_, i) => i !== idx));
  const handleUpdateSite = (idx, key, val) => {
    setSiteAddresses((s) => s.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };

  const handlePickSiteFromList = (idx, siteId) => {
    const s = availableSites.find((x) => x.id === siteId);
    if (!s) return;
    handleUpdateSite(idx, "name", s.name || "");
    handleUpdateSite(idx, "address", s.address || "");
  };

  const handlePreview = async () => {
    if (!workerId || !vehicleId) return toast.error("Selectează șofer și vehicul");
    if (!startKm) return toast.error("Completează KM Start (sau lasă să-l aducem automat din vehicul)");
    if (endKm && parseFloat(endKm) <= parseFloat(startKm)) {
      return toast.error("KM end trebuie să fie mai mare ca KM start");
    }
    const cleanSites = siteAddresses.filter((s) => s.address && s.address.trim());
    if (cleanSites.length === 0) return toast.error("Adaugă cel puțin un șantier cu adresă");

    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await axios.post(`${API}/trip-sheets/generate-monthly/preview?company_id=${companyId}`, {
        worker_id: workerId,
        vehicle_id: vehicleId,
        year: Number(year),
        month: Number(month),
        start_km: parseFloat(startKm),
        end_km: endKm ? parseFloat(endKm) : null,
        site_addresses: cleanSites,
        use_ai: useAi,
      });
      setPreview(r.data);
      toast.success(`${r.data.proposals.length} propuneri generate`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la previzualizare");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDeleteProposal = (idx) => {
    setPreview((p) => ({ ...p, proposals: p.proposals.filter((_, i) => i !== idx) }));
  };

  const handleEditProposal = (idx, key, val) => {
    setPreview((p) => ({
      ...p,
      proposals: p.proposals.map((it, i) => (i === idx ? { ...it, [key]: val } : it)),
    }));
  };

  const handleCommit = async () => {
    if (!preview || !preview.proposals.length) return toast.error("Nu există propuneri de salvat");
    setCommitting(true);
    try {
      const r = await axios.post(`${API}/trip-sheets/generate-monthly/commit?company_id=${companyId}`, {
        worker_id: workerObj.id,
        worker_name: workerObj.name,
        vehicle_plate: vehicleObj.plate,
        vehicle_name: vehicleObj.name || "",
        proposals: preview.proposals,
      });
      toast.success(`${r.data.inserted} curse salvate!`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la salvare");
    } finally {
      setCommitting(false);
    }
  };

  const finalKmTotal = preview?.proposals.reduce((s, p) => s + (Number(p.distance_km) || 0), 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="monthly-generator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-teal-700">
            <Wand2 className="w-5 h-5" />
            Generare automată foi de parcurs — luna întreagă
          </DialogTitle>
          <DialogDescription>
            Generăm automat foile pe baza zilelor pontate. Adresele șantierelor + adresa șoferului →
            calcul Google Maps. KM lipsă față de target sunt distribuiți de AI ca aprovizionări la bricolaj.
          </DialogDescription>
        </DialogHeader>

        {/* === FORM === */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Șofer *</Label>
              <Select value={workerId} onValueChange={setWorkerId}>
                <SelectTrigger data-testid="mg-worker-select"><SelectValue placeholder="Alege șofer" /></SelectTrigger>
                <SelectContent>
                  {workers.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} {w.home_address ? "✓" : "⚠️ fără adresă"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workerObj && !workerObj.home_address && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Setează adresa în Angajați → Editează
                </p>
              )}
              {workerObj?.home_address && (
                <p className="text-xs text-slate-500 mt-1">Adresă: {workerObj.home_address}</p>
              )}
            </div>
            <div>
              <Label>Vehicul *</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger data-testid="mg-vehicle-select"><SelectValue placeholder="Alege vehicul" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.plate} — {v.name || v.brand || ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Luna *</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger data-testid="mg-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RO_MONTHS.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>An *</Label>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="mg-year-input" />
            </div>
            <div>
              <Label>KM Start *</Label>
              <Input type="number" value={startKm} onChange={(e) => { setStartKm(e.target.value); setAutoPrefilledKm(null); }} placeholder="ex: 50000" data-testid="mg-start-km" />
              {autoPrefilledKm && (
                <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Adus automat: {autoPrefilledKm.value} km ({autoPrefilledKm.source === 'trip' ? 'din ultima cursă' : 'din kilometrajul vehiculului'})
                </p>
              )}
            </div>
            <div>
              <Label>KM End <span className="text-slate-400 font-normal">(opțional)</span></Label>
              <Input type="number" value={endKm} onChange={(e) => setEndKm(e.target.value)} placeholder="lasă gol — completezi pe fiecare cursă" data-testid="mg-end-km" />
              <p className="text-[11px] text-slate-500 mt-1">Dacă îl lași gol, AI-ul nu mai adaugă opriri de aprovizionare; KM end se completează ulterior.</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Șantiere / Puncte de lucru *</Label>
              <Button type="button" size="sm" variant="outline" onClick={handleAddSite} data-testid="mg-add-site">
                <Plus className="w-3 h-3 mr-1" /> Adaugă șantier
              </Button>
            </div>
            <div className="space-y-2">
              {siteAddresses.map((s, i) => (
                <div key={`site-${i}-${s.address || ''}`} className="grid grid-cols-12 gap-2 items-end">
                  {availableSites.length > 0 && (
                    <div className="col-span-3">
                      <Select onValueChange={(v) => handlePickSiteFromList(i, v)}>
                        <SelectTrigger className="text-xs"><SelectValue placeholder="Din proiecte" /></SelectTrigger>
                        <SelectContent>
                          {availableSites.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className={availableSites.length > 0 ? "col-span-3" : "col-span-4"}>
                    <Input
                      value={s.name}
                      onChange={(e) => handleUpdateSite(i, "name", e.target.value)}
                      placeholder="Nume (opțional)"
                    />
                  </div>
                  <div className={availableSites.length > 0 ? "col-span-5" : "col-span-7"}>
                    <AddressAutocomplete
                      value={s.address}
                      onChange={(v) => handleUpdateSite(i, "address", v)}
                      placeholder="Adresa șantierului"
                      testId={`mg-site-addr-${i}`}
                    />
                  </div>
                  <div className="col-span-1">
                    {siteAddresses.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => handleRemoveSite(i)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded">
            <input
              type="checkbox"
              id="use-ai"
              checked={useAi}
              onChange={(e) => setUseAi(e.target.checked)}
              className="w-4 h-4"
              data-testid="mg-use-ai"
            />
            <Label htmlFor="use-ai" className="text-sm cursor-pointer flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Folosește AI (Gemini) pentru a distribui aprovizionări la magazinele de bricolaj locale
            </Label>
          </div>

          <Button
            type="button"
            onClick={handlePreview}
            disabled={loadingPreview}
            className="w-full bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white"
            data-testid="mg-preview-btn"
          >
            {loadingPreview ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generez...</> : <><Wand2 className="w-4 h-4 mr-2" /> Generează previzualizare</>}
          </Button>
        </div>

        {/* === PREVIEW === */}
        {preview && (
          <div className="mt-4 space-y-3 border-t pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Badge variant="outline" className="justify-center">Zile pontate: <strong className="ml-1">{preview.summary.work_days_count}</strong></Badge>
              <Badge variant="outline" className="justify-center">Target km: <strong className="ml-1">{preview.summary.target_km}</strong></Badge>
              <Badge variant="outline" className="justify-center">Navetă: <strong className="ml-1">{preview.summary.base_commute_km}km</strong></Badge>
              <Badge variant="outline" className="justify-center">Extra distribuit: <strong className="ml-1">{preview.summary.extra_distributed_km}km</strong></Badge>
            </div>

            <div className="overflow-x-auto max-h-[400px] overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Dată</th>
                    <th className="p-2 text-left">Tip</th>
                    <th className="p-2 text-right">KM Start</th>
                    <th className="p-2 text-right">KM End</th>
                    <th className="p-2 text-right">Dist.</th>
                    <th className="p-2 text-left">Locație</th>
                    <th className="p-2 text-left">Scop</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {preview.proposals.map((p, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50">
                      <td className="p-2">{p.date}</td>
                      <td className="p-2">
                        <Badge variant={p.type === "supply" ? "secondary" : "outline"} className="text-xs">
                          {p.type === "supply" ? "Aprovizionare" : p.type === "extra_manual" ? "Manual" : "Navetă"}
                        </Badge>
                      </td>
                      <td className="p-2 text-right tabular-nums">{p.start_km}</td>
                      <td className="p-2 text-right tabular-nums">{p.end_km}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{p.distance_km}</td>
                      <td className="p-2 max-w-[200px] truncate" title={p.via}>{p.via}</td>
                      <td className="p-2">
                        <Input
                          value={p.purpose}
                          onChange={(e) => handleEditProposal(i, "purpose", e.target.value)}
                          className="h-7 text-xs"
                        />
                      </td>
                      <td className="p-2">
                        <Button type="button" size="icon" variant="ghost" onClick={() => handleDeleteProposal(i)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-semibold sticky bottom-0">
                  <tr>
                    <td colSpan={4} className="p-2 text-right">Total distanță:</td>
                    <td className="p-2 text-right">{finalKmTotal.toFixed(1)} km</td>
                    <td colSpan={3} className="p-2 text-xs text-slate-500">
                      KM final: {preview.summary.final_end_km} (cerut: {preview.summary.requested_end_km})
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Button
              type="button"
              onClick={handleCommit}
              disabled={committing || preview.proposals.length === 0}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="mg-commit-btn"
            >
              {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvez...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Salvează {preview.proposals.length} curse</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
